<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useRouter, useRoute } from 'vue-router';
import { DirectUpload } from 'activestorage';
import BusinessCardImportsAPI from 'dashboard/api/businessCardImports';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import BusinessCardCameraScanner from './BusinessCardCameraScanner.vue';

const emit = defineEmits(['close', 'success']);

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();

const dialogRef = ref(null);
const files = ref([]);
const uploading = ref(false);
const uploadProgress = ref(0);
const uploadedBlobIds = ref([]);
const showCamera = ref(false);

const accountId = computed(() => route.params.accountId);
const currentUser = computed(() => store.getters.getCurrentUser);

const acceptedFormats = [
  'image/jpeg',
  'image/png',
  'image/heic',
  'application/pdf',
];
const maxFileSize = 10 * 1024 * 1024; // 10MB

const canUpload = computed(() => files.value.length > 0 && !uploading.value);

const handleFileSelect = event => {
  const selectedFiles = Array.from(event.target.files || []);
  addFiles(selectedFiles);
};

const handleDrop = event => {
  event.preventDefault();
  const droppedFiles = Array.from(event.dataTransfer.files || []);
  addFiles(droppedFiles);
};

const handleDragOver = event => {
  event.preventDefault();
};

const addFiles = newFiles => {
  const validFiles = newFiles.filter(file => {
    if (!acceptedFormats.includes(file.type)) {
      useAlert(
        t('BUSINESS_CARD.UPLOAD.ERRORS.INVALID_FORMAT', { file: file.name })
      );
      return false;
    }
    if (file.size > maxFileSize) {
      useAlert(
        t('BUSINESS_CARD.UPLOAD.ERRORS.FILE_TOO_LARGE', { file: file.name })
      );
      return false;
    }
    return true;
  });

  files.value = [...files.value, ...validFiles];
};

const removeFile = index => {
  files.value.splice(index, 1);
};

const uploadFiles = async () => {
  console.log('[BusinessCard] uploadFiles called, files:', files.value.length);

  if (files.value.length === 0) {
    console.log('[BusinessCard] No files to upload');
    return;
  }

  uploading.value = true;
  uploadProgress.value = 0;
  uploadedBlobIds.value = [];

  try {
    console.log('[BusinessCard] Starting upload process...');
    console.log('[BusinessCard] Account ID:', accountId.value);
    console.log('[BusinessCard] Current User:', currentUser.value?.name);

    const uploadPromises = files.value.map((file, index) => {
      return new Promise((resolve, reject) => {
        const uploadUrl = `/api/v1/accounts/${accountId.value}/business_card_imports/direct_uploads`;
        console.log(
          '[BusinessCard] Uploading file',
          index + 1,
          ':',
          file.name,
          'to',
          uploadUrl
        );
        console.log(
          '[BusinessCard] Auth Token:',
          currentUser.value?.access_token
        );
        console.log(
          '[BusinessCard] CSRF Token:',
          document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute('content')
        );

        const upload = new DirectUpload(file, uploadUrl, {
          directUploadWillCreateBlobWithXHR: xhr => {
            xhr.setRequestHeader(
              'api_access_token',
              currentUser.value?.access_token
            );
            xhr.setRequestHeader(
              'X-CSRF-Token',
              document
                .querySelector('meta[name="csrf-token"]')
                ?.getAttribute('content')
            );
          },
        });

        upload.create((error, blob) => {
          if (error) {
            console.error(
              '[BusinessCard] Upload error for',
              file.name,
              ':',
              error
            );
            reject(error);
          } else {
            console.log(
              '[BusinessCard] Upload successful for',
              file.name,
              ', blob:',
              blob
            );
            uploadedBlobIds.value.push(blob.signed_id);
            uploadProgress.value = ((index + 1) / files.value.length) * 100;
            resolve(blob);
          }
        });
      });
    });

    await Promise.all(uploadPromises);
    console.log(
      '[BusinessCard] All uploads complete, blob IDs:',
      uploadedBlobIds.value
    );

    // Create import
    console.log('[BusinessCard] Creating import record...');
    const result = await store.dispatch('businessCardImports/create', {
      fileIds: uploadedBlobIds.value,
    });

    console.log('[BusinessCard] Import created successfully:', result);
    useAlert(t('BUSINESS_CARD.UPLOAD.SUCCESS'));
    emit('success', result);
    handleClose();
  } catch (error) {
    console.error('[BusinessCard] Upload failed:', error);
    console.error('[BusinessCard] Error details:', {
      message: error.message,
      response: error.response,
      stack: error.stack,
    });
    useAlert(
      error?.response?.data?.error ||
        t('BUSINESS_CARD.UPLOAD.ERRORS.UPLOAD_FAILED')
    );
  } finally {
    uploading.value = false;
    uploadProgress.value = 0;
  }
};

const handleCameraCaptures = async capturedFiles => {
  // Add captured files to the files list
  files.value = [...files.value, ...capturedFiles];
  showCamera.value = false;
};

const open = () => {
  dialogRef.value?.open();
};

const close = () => {
  files.value = [];
  uploadedBlobIds.value = [];
  uploadProgress.value = 0;
  showCamera.value = false;
  dialogRef.value?.close();
};

const onCancel = () => {
  dialogRef.value?.close();
};

const handleClose = () => {
  // Clean up state but don't call close() to avoid infinite loop
  files.value = [];
  uploadedBlobIds.value = [];
  uploadProgress.value = 0;
  showCamera.value = false;
  emit('close');
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('BUSINESS_CARD.UPLOAD.TITLE')"
    :description="t('BUSINESS_CARD.UPLOAD.DESCRIPTION')"
    :max-width="showCamera ? 'max-w-5xl' : 'max-w-lg'"
    @close="handleClose"
  >
    <template #default>
      <div class="flex flex-col md:flex-row gap-6 transition-all duration-300">
        <!-- Left Side: Upload Form -->
        <div class="flex flex-col gap-4 flex-1 min-w-0">
          <!-- Camera Scanner Button (Mobile Only) -->
          <div class="md:hidden flex items-center justify-center">
            <Button
              icon="i-lucide-camera"
              variant="outline"
              color="blue"
              size="md"
              :label="
                showCamera
                  ? t('BUSINESS_CARD.CAMERA.HIDE_CAMERA')
                  : t('BUSINESS_CARD.UPLOAD.SCAN_WITH_CAMERA')
              "
              @click="showCamera = !showCamera"
            />
          </div>

          <!-- Camera View (Mobile Only) -->
          <div
            v-if="showCamera"
            class="md:hidden w-full border-b border-n-weak pb-4 mb-2"
          >
            <BusinessCardCameraScanner
              :open="true"
              :multiple="true"
              :embedded="true"
              @captures="handleCameraCaptures"
              @error="showCamera = false"
            />
          </div>

          <!-- Desktop Camera Toggle -->
          <div class="hidden md:flex items-center justify-center">
            <Button
              icon="i-lucide-camera"
              variant="outline"
              color="blue"
              size="md"
              :label="
                showCamera
                  ? t('BUSINESS_CARD.CAMERA.HIDE_CAMERA')
                  : t('BUSINESS_CARD.UPLOAD.SCAN_WITH_CAMERA')
              "
              @click="showCamera = !showCamera"
            />
          </div>

          <div class="relative">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-n-weak" />
            </div>
            <div class="relative flex justify-center text-xs uppercase">
              <span class="px-2 bg-n-background text-n-slate-10">
                {{ t('BUSINESS_CARD.UPLOAD.OR') }}
              </span>
            </div>
          </div>

          <!-- File Drop Zone -->
          <div
            class="flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-lg border-n-slate-6 bg-n-alpha-2 hover:border-n-slate-8 transition-colors"
            @drop="handleDrop"
            @dragover="handleDragOver"
            @dragenter.prevent
          >
            <Icon icon="i-lucide-upload" class="size-12 text-n-slate-11 mb-4" />
            <p class="text-sm text-n-slate-11 mb-2">
              {{ t('BUSINESS_CARD.UPLOAD.DROP_ZONE_TEXT') }}
            </p>
            <p class="text-xs text-n-slate-10 mb-4">
              {{ t('BUSINESS_CARD.UPLOAD.SUPPORTED_FORMATS') }}
            </p>
            <Button
              variant="outline"
              color="slate"
              size="sm"
              :label="t('BUSINESS_CARD.UPLOAD.SELECT_FILES')"
              @click="$refs.fileInput.click()"
            />
            <input
              ref="fileInput"
              type="file"
              multiple
              :accept="acceptedFormats.join(',')"
              class="hidden"
              @change="handleFileSelect"
            />
          </div>

          <!-- Upload Progress -->
          <div v-if="uploading" class="flex flex-col gap-2">
            <div class="flex items-center justify-between text-sm">
              <span class="text-n-slate-11">{{
                t('BUSINESS_CARD.UPLOAD.UPLOADING')
              }}</span>
              <span class="text-n-slate-11">{{ Math.round(uploadProgress) }}%</span>
            </div>
            <div class="h-2 bg-n-slate-4 rounded-full overflow-hidden">
              <div
                class="h-full bg-n-blue-9 transition-all duration-300"
                :style="{ width: `${uploadProgress}%` }"
              />
            </div>
          </div>

          <!-- Selected Files List -->
          <div v-if="files.length > 0" class="flex flex-col gap-2">
            <h3 class="text-sm font-medium text-n-slate-12">
              {{
                t('BUSINESS_CARD.UPLOAD.SELECTED_FILES', {
                  count: files.length,
                })
              }}
            </h3>
            <div class="flex flex-col gap-2 max-h-48 overflow-y-auto">
              <div
                v-for="(file, index) in files"
                :key="index"
                class="flex items-center justify-between p-2 bg-n-alpha-2 rounded-lg"
              >
                <div class="flex items-center gap-2 flex-1 min-w-0">
                  <Icon
                    icon="i-lucide-file"
                    class="size-4 text-n-slate-11 flex-shrink-0"
                  />
                  <span class="text-sm text-n-slate-12 truncate">{{
                    file.name
                  }}</span>
                  <span class="text-xs text-n-slate-10">
                    ({{ (file.size / 1024 / 1024).toFixed(2) }} MB)
                  </span>
                </div>
                <Button
                  icon="i-lucide-x"
                  variant="ghost"
                  size="xs"
                  color="slate"
                  @click="removeFile(index)"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Right Side: Camera Scanner (Desktop Only) -->
        <div
          v-if="showCamera"
          class="hidden md:flex flex-col w-1/2 border-l border-n-weak pl-6"
        >
          <h3 class="text-sm font-medium text-n-slate-12 mb-4">
            {{ t('BUSINESS_CARD.CAMERA.TITLE') }}
          </h3>
          <BusinessCardCameraScanner
            :open="true"
            :multiple="true"
            :embedded="true"
            @captures="handleCameraCaptures"
            @error="showCamera = false"
          />
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex items-center justify-end gap-2">
        <Button
          variant="faded"
          color="slate"
          :label="t('BUTTON.CANCEL')"
          @click="onCancel"
        />
        <Button
          :label="t('BUSINESS_CARD.UPLOAD.UPLOAD_BUTTON')"
          :disabled="!canUpload"
          :is-loading="uploading"
          @click="uploadFiles"
        />
      </div>
    </template>
  </Dialog>
</template>
